// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import Observer, { ObserverCallback, ObserverDestructor } from "../nor/ts/Observer";
import LogService from "../nor/ts/LogService";
import JsonAny from "../nor/ts/Json";
import {
    CREATE_AGENT_ACCOUNT_URL,
    CREATE_AGENT_POOL_URL,
    DELETE_AGENT_POOL_URL,
    GET_AGENT_POOL_URL,
    LIST_AGENT_POOLS_URL,
    UPDATE_AGENT_POOL_URL
} from "../constants/form-backend";
import RequestHeaderName from "../nor/ui/types/RequestHeaderName";
import SessionService from "./SessionService";
import RepositoryEntry from "../nor/ts/simpleRepository/types/RepositoryEntry";
import ResponseDTO, { isResponseDTO } from "../nor/ui/types/ResponseDTO";
import { isResponseListDTO } from "../nor/ui/types/ResponseListDTO";
import { map } from "../nor/ts/modules/lodash";
import FormLoginModal from "../modals/FormLoginModal";
import AgentPoolModel from "../nor/pipeline/types/AgentPoolModel";
import AgentPoolDTO, { isAgentPoolDTO } from "../nor/pipeline/dto/AgentPoolDTO";
import AgentAccountModel from "../nor/pipeline/types/AgentAccountModel";
import AgentAccountDTO, { isAgentAccountDTO } from "../nor/pipeline/dto/AgentAccountDTO";
import HttpService from "./HttpService";

const LOG = LogService.createLogger('AgentService');

export enum AgentServiceEvent {

    POOL_CREATED = "AgentService:poolCreated",
    ACCOUNT_CREATED = "AgentService:accountCreated"

}

export type AgentServiceDestructor = ObserverDestructor;

export class AgentService {

    private static _observer: Observer<AgentServiceEvent> = new Observer<AgentServiceEvent>("AgentService");

    public static Event = AgentServiceEvent;

    public static on (
        name: AgentServiceEvent,
        callback: ObserverCallback<AgentServiceEvent>
    ): AgentServiceDestructor {
        return this._observer.listenEvent(name, callback);
    }

    public static destroy (): void {
        this._observer.destroy();
    }

    public static createAgentPool (
        model     : AgentPoolModel
    ) {

        const accessKey = SessionService.getSessionId();

        if (!accessKey) throw new Error(`No session!`);

        const body : AgentPoolDTO = {
            model    : model
        };

        HttpService.postJson(`${CREATE_AGENT_POOL_URL}`, body as unknown as JsonAny, {
            [RequestHeaderName.AUTHORIZATION]: `Bearer ${accessKey}`
        }).then( (response : any | undefined) => {

            if ( !isResponseDTO<AgentPoolDTO>(response, isAgentPoolDTO) ) {
                throw new TypeError(`Payload was not FormResponseDTO: "${response}"`);
            }

            LOG.debug(`Pool created: `, response);

            this._observer.triggerEvent(AgentServiceEvent.POOL_CREATED, response?.id);

        }).catch( (err : any) => {

            LOG.error(`Create error: `, err);

            // FIXME: Create error popup

        });

    }

    public static createAgentAccount (
        agentPoolId  : string,
        model       ?: AgentAccountModel
    ) {

        const accessKey = SessionService.getSessionId();
        if (!accessKey) throw new Error(`No session!`);

        const body : AgentAccountDTO = {
            model    : model
        };

        HttpService.postJson(`${CREATE_AGENT_ACCOUNT_URL(agentPoolId)}`, body as unknown as JsonAny, {
            [RequestHeaderName.AUTHORIZATION]: `Bearer ${accessKey}`
        }).then( (response : any | undefined) => {

            if ( !isResponseDTO<AgentAccountDTO>(response, isAgentAccountDTO) ) {
                throw new TypeError(`Payload was not ResponseDTO of AgentAccountDTO: "${response}"`);
            }

            LOG.debug(`Account created: `, response);

            this._observer.triggerEvent(AgentServiceEvent.ACCOUNT_CREATED, response?.payload);

        }).catch( (err : any) => {

            LOG.error(`Create account error: `, err);

            // FIXME: Create error popup

        });

    }

    public static async getAgentPoolList () : Promise<RepositoryEntry<AgentPoolDTO>[]> {

        const accessKey = SessionService.getSessionId();

        if (!accessKey) throw new Error(`No session!`);

        const response : any | undefined = await HttpService.getJson(
            `${LIST_AGENT_POOLS_URL}`,
            {
                [RequestHeaderName.AUTHORIZATION]: `Bearer ${accessKey}`
            }
        );

        if ( !isResponseListDTO<AgentPoolDTO>(response, isAgentPoolDTO) ) {
            LOG.debug(`response = `, response);
            throw new TypeError(`Payload was not ListResponseDTO<AgentDTO>: "${response}"`);
        }

        return map(response.payload, (item: ResponseDTO<AgentPoolDTO>) : RepositoryEntry<AgentPoolDTO> => {
            return {
                data: item.payload,
                id: item.id,
                version: item.version,
                deleted: item.deleted
            }
        });

    }

    public static async getAgentPool (id: string) : Promise<RepositoryEntry<AgentPoolDTO>> {

        const accessKey = SessionService.getSessionId();
        if (!accessKey) {
            FormLoginModal.openModal();
            throw new Error(`AgentService.getAgent: No session!`);
        }

        LOG.debug(`getAgentPool: `, id);

        const response : any | undefined = await HttpService.getJson(
            `${GET_AGENT_POOL_URL(id)}`,
            {
                [RequestHeaderName.AUTHORIZATION]: `Bearer ${accessKey}`
            }
        );

        if ( !isResponseDTO<AgentPoolDTO>(response, isAgentPoolDTO) ) {
            LOG.debug(`response = `, response);
            throw new TypeError(`getAgent: Payload was not ResponseDTO<AgentDTO>: "${response}"`);
        }

        LOG.debug(`getAgentPool: received: `, response);

        return {
            id: id,
            version: response.version,
            data: response.payload,
            deleted: response.deleted
        };

    }

    public static async saveAgentPool (
        id    : string,
        model : AgentPoolModel
    ) : Promise<RepositoryEntry<AgentPoolDTO>> {

        const accessKey = SessionService.getSessionId();
        if (!accessKey) throw new Error(`No session!`);

        const body : AgentPoolDTO = {
            model    : model
        };

        const response : any | undefined = await HttpService.postJson(
            `${UPDATE_AGENT_POOL_URL(id)}`,
            body as unknown as JsonAny,
            {
                [RequestHeaderName.AUTHORIZATION]: `Bearer ${accessKey}`
            }
        );

        if ( !isResponseDTO<AgentPoolDTO>(response, isAgentPoolDTO) ) {
            LOG.debug(`response = `, response);
            throw new TypeError(`Payload was not ResponseDTO<AgentDTO>: "${response}"`);
        }

        LOG.debug(`Updated pool received: `, response);

        return {
            id: id,
            version: response.version,
            data: response.payload,
            deleted: response.deleted
        };

    }

    public static async deleteAgentPool (
        id        : string
    ) : Promise<string> {

        const accessKey = SessionService.getSessionId();
        if (!accessKey) throw new Error(`No session!`);

        const response : any | undefined = await HttpService.deleteJson(
            `${DELETE_AGENT_POOL_URL(id)}`,
            {
                [RequestHeaderName.AUTHORIZATION]: `Bearer ${accessKey}`
            }
        );

        if ( !isResponseDTO<AgentPoolDTO>(response, isAgentPoolDTO) ) {
            LOG.debug(`response = `, response);
            throw new TypeError(`Payload was not FormResponseDTO: "${response}"`);
        }

        LOG.debug(`Deleted pool successfully: `, response);

        return response.id;

    }

}

export default AgentService;
